// @ts-nocheck
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/lib/toast';
import { AVAILABLE_COLUMNS } from '@/lib/listViewColumns';

export type SortConfig = {
	column: string;
	direction: 'asc' | 'desc';
} | null;

interface CollectionItem {
	card: any;
	normalQuantity: number;
	foilQuantity: number;
	// Enrichment data to avoid refetching card details
	enrichmentData?: {
		image_small: string;
		image_normal: string;
		image_large: string;
		set_code: string;
		rarity: string;
		finish: string;
		collector_number: string;
		price_usd: number | null;
		price_usd_stable: number | null;
		price_usd_foil: number | null;
		price_usd_foil_stable: number | null;
		released_at: string;
		is_promo: boolean;
		sets: { name: string; code: string; icon_url: string };
	};
	lastEnriched?: number; // Timestamp for cache invalidation
}

export interface Filters {
	textSearch: string;
	selectedSets: string[];
	rarities: string[];
	inkColors: string[];
	types: string[];
	costMin: number;
	costMax: number;
	priceMin: number;
	priceMax: number | null;
	priceFinishes: ('normal' | 'foil')[];
	format: 'core' | 'all';
	showPromos: boolean;
	sortBy:
		| 'name-asc'
		| 'name-desc'
		| 'cost-asc'
		| 'cost-desc'
		| 'price-asc'
		| 'price-desc'
		// | 'collector-number'
		// | 'rarity'
		| 'released_at-desc';
	inkwell: ('inkable' | 'inkless')[];
	keywords: string[];
	classifications: string[];
	strengthMin: number;
	strengthMax: number;
	willpowerMin: number;
	willpowerMax: number;
	loreMin: number;
	loreMax: number;
	prioritizeDualInk: boolean;
}

interface CardStore {
	viewMode: 'all' | 'collection' | 'sets';
	displayMode: 'grid' | 'list';
	useExperimentalListView: boolean;
	filters: Filters;
	collection: Record<string, CollectionItem>;
	userId: string | null;
	isFilterPanelCollapsed: boolean;
	isFilterPanelTransitioning: boolean;
	isViewModeTransitioning: boolean;
	isDeckBuilderTransitioning: boolean;
	visibleColumns: string[];
	sortConfig: SortConfig;
	isDeckBuilderActive: boolean;
	advancedFiltersOpen: boolean;
	setViewMode: (mode: 'all' | 'collection' | 'sets') => void;
	setDisplayMode: (mode: 'grid' | 'list') => void;
	toggleExperimentalListView: () => void;
	setFilters: (filters: Partial<Filters>) => void;
	clearFilters: () => void;
	addToCollection: (card: any, isFoil: boolean) => Promise<void>;
	removeFromCollection: (cardId: string, isFoil: boolean) => Promise<void>;
	setQty: (cardId: string, finish: 'normal' | 'foil', qty: number) => void;
	setUserId: (userId: string | null) => void;
	syncCollectionFromDatabase: () => Promise<void>;
	toggleFilterPanel: () => void;
	setFilterPanelTransitioning: (isTransitioning: boolean) => void;
	setViewModeTransitioning: (isTransitioning: boolean) => void;
	setDeckBuilderTransitioning: (isTransitioning: boolean) => void;
	clearCollection: () => void;
	toggleColumn: (columnId: string) => void;
	isColumnVisible: (columnId: string) => boolean;
	resetColumnsToDefaults: () => void;
	handleColumnSort: (columnId: string) => void;
	clearSort: () => void;
	setDeckBuilderActive: (isActive: boolean) => void;
	setAdvancedFiltersOpen: (isOpen: boolean) => void;
}

export const defaultFilters: Filters = {
	textSearch: '',
	selectedSets: [],
	rarities: [],
	inkColors: [],
	types: [],
	costMin: 0,
	costMax: 10,
	priceMin: 0,
	priceMax: null,
	priceFinishes: ['normal'],
	format: 'core',
	showPromos: false,
	sortBy: 'price-desc',
	inkwell: ['inkable', 'inkless'],
	keywords: [],
	classifications: [],
	strengthMin: 0,
	strengthMax: 12,
	willpowerMin: 1,
	willpowerMax: 12,
	prioritizeDualInk: false,
	loreMin: 0,
	loreMax: 5
};

// Helper to validate enrichment data structure
const isValidEnrichmentData = (data: any): boolean => {
	if (!data) return false;

	// Check for required image fields (most critical for display)
	if (!data.image_large || !data.image_normal || !data.image_small) {
		return false;
	}

	// Check for required metadata fields
	if (!data.set_code || !data.rarity || !data.finish) {
		return false;
	}

	return true;
};

// Cache expiration time (7 days in milliseconds)
const ENRICHMENT_CACHE_EXPIRATION = 7 * 24 * 60 * 60 * 1000;

// Sync debouncing flag and cooldown
let isSyncing = false;
let lastSyncTime = 0;
const SYNC_COOLDOWN = 5000; // 5 seconds

// Track last local change for merge logic
let lastLocalChangeAt: number | null = null;

// Queue for batched database updates
let updateQueue: Array<{ cardId: string; normalQty: number; foilQty: number; userId: string }> = [];
let deleteQueue: Array<{ cardId: string; userId: string }> = [];
let batchTimeout: ReturnType<typeof setTimeout> | null = null;
const BATCH_DELAY = 2000; // 2 second batch delay - gives more time for writes to complete

// Legacy: processBatchedUpdates disabled - user_collections table no longer exists
// TCG collection is now managed via user_items table
const processBatchedUpdates = async () => {
	// Clear queues without processing - legacy table removed
	updateQueue = [];
	deleteQueue = [];
};

// Debounce timers for individual card updates
const updateDebounceTimers = new Map<string, ReturnType<typeof setTimeout>>();

const queueDatabaseUpdate = (
	cardId: string,
	normalQty: number,
	foilQty: number,
	userId: string
) => {
	// Clear existing timer for this card
	const existingTimer = updateDebounceTimers.get(cardId);
	if (existingTimer) {
		clearTimeout(existingTimer);
	}

	// Debounce rapid updates (300ms)
	const timer = setTimeout(() => {
		updateDebounceTimers.delete(cardId);

		// Remove from delete queue if present
		deleteQueue = deleteQueue.filter((item) => item.cardId !== cardId);

		// Check if already queued with same values
		const existingIndex = updateQueue.findIndex((item) => item.cardId === cardId);
		if (existingIndex >= 0) {
			const existing = updateQueue[existingIndex];
			if (existing.normalQty === normalQty && existing.foilQty === foilQty) {
				return; // Skip duplicate update
			}
			updateQueue[existingIndex] = { cardId, normalQty, foilQty, userId };
		} else {
			updateQueue.push({ cardId, normalQty, foilQty, userId });
		}

		// Schedule batch processing
		if (batchTimeout) clearTimeout(batchTimeout);
		batchTimeout = setTimeout(processBatchedUpdates, BATCH_DELAY);
	}, 300);

	updateDebounceTimers.set(cardId, timer);
};

const queueDatabaseDelete = (cardId: string, userId: string) => {
	// Remove from update queue if present
	updateQueue = updateQueue.filter((item) => item.cardId !== cardId);

	// Add to delete queue
	if (!deleteQueue.some((item) => item.cardId === cardId)) {
		deleteQueue.push({ cardId, userId });
	}

	// Schedule batch processing
	if (batchTimeout) clearTimeout(batchTimeout);
	batchTimeout = setTimeout(processBatchedUpdates, BATCH_DELAY);
};

// Flush pending writes before sync
const flushPendingWrites = async (): Promise<boolean> => {
	const hasPending = updateQueue.length > 0 || deleteQueue.length > 0 || batchTimeout !== null;

	if (hasPending) {
		// Clear timeout to process immediately
		if (batchTimeout) {
			clearTimeout(batchTimeout);
			batchTimeout = null;
		}

		// Process batched updates
		await processBatchedUpdates();

		// Short delay for read-your-writes consistency
		await new Promise((resolve) => setTimeout(resolve, 150));
	}

	return hasPending;
};

export const useCardStore = create<CardStore>()(
	persist(
		(set, get) => ({
			viewMode: 'all',
			displayMode: 'grid',
			useExperimentalListView: false,
			filters: defaultFilters,
			collection: {},
			userId: null,
			isFilterPanelCollapsed: false,
			isFilterPanelTransitioning: false,
			isViewModeTransitioning: false,
			isDeckBuilderTransitioning: false,
			isDeckBuilderActive: false,
			advancedFiltersOpen: false,
			visibleColumns: AVAILABLE_COLUMNS.filter((col) => col.defaultVisible).map((col) => col.id),
			sortConfig: null,
			setViewMode: (mode) =>
				set((state) => {
					const previousMode = state.viewMode;
					const updates: any = { viewMode: mode };

					// When switching TO collection view, auto-switch to Infinity format with promos
					if (previousMode !== 'collection' && mode === 'collection') {
						updates.filters = {
							...state.filters,
							format: 'all',
							showPromos: true
						};
					}

					return updates;
				}),
			setDisplayMode: (mode) => set({ displayMode: mode }),
			toggleExperimentalListView: () =>
				set((state) => ({ useExperimentalListView: !state.useExperimentalListView })),
			setFilters: (newFilters) =>
				set((state) => ({
					filters: { ...state.filters, ...newFilters }
				})),
			clearFilters: () => set({ filters: defaultFilters, sortConfig: null }),
			setUserId: (userId) => set({ userId }),
			toggleFilterPanel: () =>
				set((state) => ({ isFilterPanelCollapsed: !state.isFilterPanelCollapsed })),
			setFilterPanelTransitioning: (isTransitioning: boolean) =>
				set({ isFilterPanelTransitioning: isTransitioning }),
			setViewModeTransitioning: (isTransitioning: boolean) =>
				set({ isViewModeTransitioning: isTransitioning }),
			setDeckBuilderTransitioning: (isTransitioning: boolean) =>
				set({ isDeckBuilderTransitioning: isTransitioning }),
			setDeckBuilderActive: (isActive: boolean) => set({ isDeckBuilderActive: isActive }),
			clearCollection: () => set({ collection: {} }),

			toggleColumn: (columnId: string) => {
				const state = get();
				const current = state.visibleColumns;
				if (current.includes(columnId)) {
					// Don't allow hiding all columns
					if (current.length === 1) return;
					set({ visibleColumns: current.filter((id) => id !== columnId) });
				} else {
					set({ visibleColumns: [...current, columnId] });
				}
			},

			isColumnVisible: (columnId: string) => {
				const exists = AVAILABLE_COLUMNS.some((c) => c.id === columnId);
				return exists && get().visibleColumns.includes(columnId);
			},

			resetColumnsToDefaults: () => {
				const defaults = AVAILABLE_COLUMNS.filter((col) => col.defaultVisible).map((col) => col.id);
				set({ visibleColumns: defaults });
			},

			handleColumnSort: (columnId: string) => {
				const column = AVAILABLE_COLUMNS.find((col) => col.id === columnId);
				if (!column?.sortable) return;

				const currentSort = get().sortConfig;
				if (currentSort?.column === columnId) {
					if (currentSort.direction === 'asc') {
						set({ sortConfig: { column: columnId, direction: 'desc' } });
					} else {
						set({ sortConfig: null });
					}
				} else {
					set({ sortConfig: { column: columnId, direction: 'asc' } });
				}
			},

			clearSort: () => {
				set({ sortConfig: null });
			},

			setAdvancedFiltersOpen: (isOpen: boolean) => {
				set({ advancedFiltersOpen: isOpen });
			},

			addToCollection: async (card, isFoil = false) => {
				const state = get();

				if (!state.userId) {
					throw new Error('AUTH_REQUIRED');
				}

				// CRITICAL: Validate that we have a printing, not a canonical card
				if (!card?.id || !card?.canonical_card_id) {
					console.error('[CardStore] Cannot add - invalid card object:', {
						cardId: card?.id,
						canonicalId: card?.canonical_card_id,
						cardName: card?.name
					});
					toast.error('Please select a specific printing before adding');
					return;
				}

				// Auto-correct finish if mismatch detected
				let correctedIsFoil = isFoil;
				if (card.finish === 'foil' && !isFoil) {
					correctedIsFoil = true;
					toast.info('This printing is foil-only. Added as foil.');
				} else if (card.finish === 'nonfoil' && isFoil) {
					correctedIsFoil = false;
					toast.info('This printing is non-foil only. Added as normal.');
				}

				const existing = state.collection[card.id];

				// Extract enrichment data from card if available
				let enrichmentData = undefined;
				if (card.image_large) {
					const potentialEnrichmentData = {
						image_small: card.image_small,
						image_normal: card.image_normal,
						image_large: card.image_large,
						set_code: card.set_code,
						rarity: card.rarity,
						finish: card.finish,
						collector_number: card.collector_number,
						price_usd: card.price_usd,
						price_usd_stable: card.price_usd_stable,
						price_usd_foil: card.price_usd_foil,
						price_usd_foil_stable: card.price_usd_foil_stable,
						released_at: card.released_at,
						is_promo: card.is_promo,
						sets: card.sets
					};

					// Validate before storing
					if (isValidEnrichmentData(potentialEnrichmentData)) {
						enrichmentData = potentialEnrichmentData;
					} else {
						// Fall back to existing enrichment data if new data is invalid
						enrichmentData = existing?.enrichmentData;
						console.warn('[CardStore] Invalid enrichment data when adding card:', card.id);
					}
				} else {
					// Preserve existing enrichment data if card doesn't have new data
					enrichmentData = existing?.enrichmentData;
				}

				const newCollection = {
					...state.collection,
					[card.id]: {
						card,
						normalQuantity: correctedIsFoil
							? existing?.normalQuantity || 0
							: (existing?.normalQuantity || 0) + 1,
						foilQuantity: correctedIsFoil
							? (existing?.foilQuantity || 0) + 1
							: existing?.foilQuantity || 0,
						enrichmentData,
						lastEnriched: enrichmentData ? Date.now() : existing?.lastEnriched
					}
				};

				// Optimistic update: Update UI immediately
				set({ collection: newCollection });

				// Track local change
				lastLocalChangeAt = Date.now();

				// Queue database write if user is authenticated
				if (state.userId) {
					const item = newCollection[card.id];
					queueDatabaseUpdate(card.id, item.normalQuantity, item.foilQuantity, state.userId);
				}
			},

			removeFromCollection: async (cardId, isFoil = false) => {
				const state = get();

				if (!state.userId) {
					throw new Error('AUTH_REQUIRED');
				}

				const newCollection = { ...state.collection };
				const existing = newCollection[cardId];

				if (!existing) return;

				const newNormal = isFoil
					? existing.normalQuantity
					: Math.max(0, existing.normalQuantity - 1);
				const newFoil = isFoil ? Math.max(0, existing.foilQuantity - 1) : existing.foilQuantity;

				// Always update the collection, never delete from local state
				newCollection[cardId] = {
					...existing,
					normalQuantity: newNormal,
					foilQuantity: newFoil
				};

				// Optimistic update: Update UI immediately
				set({ collection: newCollection });

				// Track local change
				lastLocalChangeAt = Date.now();

				// For database: if both quantities are 0, delete the row
				// But keep it in local state so UI doesn't break
				if (state.userId) {
					if (newNormal === 0 && newFoil === 0) {
						queueDatabaseDelete(cardId, state.userId);
					} else {
						queueDatabaseUpdate(cardId, newNormal, newFoil, state.userId);
					}
				}
			},

			setQty: (cardId, finish, qty) => {
				const state = get();

				if (!state.userId) {
					throw new Error('AUTH_REQUIRED');
				}

				const existing = state.collection[cardId];

				if (!existing) {
					console.warn('[CardStore] setQty called for non-existent entry:', {
						cardId,
						finish,
						qty,
						existingKeys: Object.keys(state.collection).slice(0, 10)
					});
					return;
				}

				const newNormal = finish === 'normal' ? qty : existing.normalQuantity;
				const newFoil = finish === 'foil' ? qty : existing.foilQuantity;

				if (newNormal === 0 && newFoil === 0) {
					// Delete entry
					const newCollection = { ...state.collection };
					delete newCollection[cardId];
					set({ collection: newCollection });

					// Track local change
					lastLocalChangeAt = Date.now();

					// Queue database delete if user is authenticated
					if (state.userId) {
						queueDatabaseDelete(cardId, state.userId);
					}
				} else {
					// Update entry
					const newCollection = {
						...state.collection,
						[cardId]: {
							...existing,
							normalQuantity: newNormal,
							foilQuantity: newFoil
						}
					};
					set({ collection: newCollection });

					// Track local change
					lastLocalChangeAt = Date.now();

					// Queue database update if user is authenticated
					if (state.userId) {
						queueDatabaseUpdate(cardId, newNormal, newFoil, state.userId);
					}
				}
			},

			// Legacy: syncCollectionFromDatabase disabled - user_collections table no longer exists
			// TCG collection is now managed via user_items table with the marketplace system
			syncCollectionFromDatabase: async () => {
				// No-op: Legacy collection sync disabled
				// The card collection feature has been replaced by the user_items inventory system
				return;
			}
		}),
		{
			name: 'lorcana-collection',
			version: 6,
			partialize: (state) => ({
				// Only persist collection if user is authenticated
				collection: state.userId ? state.collection : {},
				// Always persist these:
				viewMode: state.viewMode,
				displayMode: state.displayMode,
				filters: state.filters,
				visibleColumns: state.visibleColumns,
				sortConfig: state.sortConfig,
				isFilterPanelCollapsed: state.isFilterPanelCollapsed,
				useExperimentalListView: state.useExperimentalListView,
				advancedFiltersOpen: state.advancedFiltersOpen
				// Don't persist userId (comes from auth session)
			}),
			migrate: (persistedState: any, version: number) => {
				// Reset to clean defaults on v5 to clear stale state
				if (version < 6) {
					return {
						...persistedState,
						filters: defaultFilters,
						collection: persistedState.userId ? persistedState.collection : {}
					};
				}
				return persistedState as CardStore;
			}
		}
	)
);
